import { useState, forwardRef } from "react";
import { useDemo } from "@/contexts/DemoContext";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Rocket,
  Gift,
  Truck,
  Percent,
  ShoppingBag,
  ArrowRight,
  X,
  Crown,
  Sparkles,
  CheckCircle2,
} from "lucide-react";

/**
 * Shows after demo expires OR when balance hits 0.
 * Two surfaces:
 *  1. Inline banner (always visible when expired)
 *  2. Full modal (shown once, dismissible)
 */
export const DemoConversionCTA = forwardRef<HTMLDivElement>((_, ref) => {
  const { demoWallet, isExpired, isDemoActive } = useDemo();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [modalDismissed, setModalDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("chawni-demo-cta-dismissed") === "true";
  });

  const handleDismiss = () => {
    localStorage.setItem("chawni-demo-cta-dismissed", "true");
    setModalDismissed(true);
  };

  const showCTA = !!user && !!demoWallet && (isExpired || (!isDemoActive && demoWallet.balance <= 0));

  if (!showCTA || modalDismissed) return null;

  const incentives = [
    {
      icon: Percent,
      title: "10% off your first order",
      description: "Use code DEMO10 at checkout",
      color: "text-emerald-600",
      bg: "bg-emerald-500/10",
    },
    {
      icon: Truck,
      title: "Free delivery",
      description: "On your first real purchase",
      color: "text-sky-600",
      bg: "bg-sky-500/10",
    },
    {
      icon: Gift,
      title: "100 Loyalty Points",
      description: "Bonus points to get you started",
      color: "text-violet-600",
      bg: "bg-violet-500/10",
    },
  ];

  return (
    <div ref={ref} className="contents">
      {/* ── Sticky top banner ── */}
      <div className="bg-gradient-to-r from-amber-500/15 via-rose-500/10 to-violet-500/15 border-b border-amber-500/20">
        <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-[10px] font-bold tracking-wider">
              DEMO ENDED
            </Badge>
            <span className="text-sm text-foreground font-medium hidden sm:inline">
              Ready to buy for real? Enjoy exclusive first-purchase perks!
            </span>
          </div>
          <Button
            size="sm"
            onClick={() => navigate("/shop")}
            className="gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground text-xs"
          >
            <ShoppingBag className="w-3.5 h-3.5" />
            Shop Now
            <ArrowRight className="w-3 h-3" />
          </Button>
        </div>
      </div>

      {/* ── One-time modal ── */}
      <Dialog open={!modalDismissed} onOpenChange={(open) => { if (!open) handleDismiss(); }}>
        <DialogContent className="sm:max-w-md border-none bg-background p-0 overflow-hidden">
          {/* Header gradient */}
          <div className="bg-gradient-to-br from-primary/10 via-accent/10 to-violet-500/10 px-6 pt-6 pb-4 text-center relative">
            <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-primary/10 flex items-center justify-center">
              <Crown className="w-8 h-8 text-primary" />
            </div>
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-foreground">
                Your Demo Has Ended
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground mt-1">
              You explored like a pro! Now unlock real purchases with these exclusive perks.
            </p>
          </div>

          {/* Incentives */}
          <div className="px-6 py-4 space-y-3">
            {incentives.map(({ icon: Icon, title, description, color, bg }) => (
              <div
                key={title}
                className="flex items-center gap-3 p-3 rounded-xl border border-border/50 bg-muted/30"
              >
                <div className={`w-10 h-10 rounded-lg ${bg} flex items-center justify-center shrink-0`}>
                  <Icon className={`w-5 h-5 ${color}`} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">{title}</p>
                  <p className="text-xs text-muted-foreground">{description}</p>
                </div>
                <CheckCircle2 className="w-4 h-4 text-emerald-500 ml-auto shrink-0" />
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="px-6 pb-6 space-y-2">
            <Button
              className="w-full gap-2 text-sm"
              onClick={() => {
                handleDismiss();
                navigate("/shop");
              }}
            >
              <Sparkles className="w-4 h-4" />
              Start Shopping for Real
            </Button>
            <Button
              variant="ghost"
              className="w-full text-xs text-muted-foreground"
              onClick={handleDismiss}
            >
              Maybe later
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
});

DemoConversionCTA.displayName = "DemoConversionCTA";
